import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets document title, meta description, OG and Twitter tags.
 * Avoids react-helmet to keep the dependency footprint small.
 */
export default function useSEO({ title, description, image, url }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let tag = document.querySelector(`meta[${attr}='${name}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    if (image) {
      // Always emit an absolute URL — link-preview crawlers (WhatsApp,
      // iMessage, etc.) won't follow relative paths.
      const absolute = image.startsWith("http")
        ? image
        : `${window.location.origin}${image.startsWith("/") ? "" : "/"}${image}`;
      setMeta("og:image", absolute, "property");
      setMeta("twitter:image", absolute);
    }
    if (url) setMeta("og:url", url, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Canonical → always the non-www version (fixes www/non-www duplicate content)
    const canonicalHref = `https://sugaspice.uk${window.location.pathname}`.replace(/\/$/, "") || "https://sugaspice.uk";
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.pathname === "/" ? "https://sugaspice.uk/" : canonicalHref);
    setMeta("og:url", window.location.pathname === "/" ? "https://sugaspice.uk/" : canonicalHref, "property");
  }, [title, description, image, url]);
}
